import { colors, type } from "../styles";

/**
 * A small segmented control, styled exactly like `DateRangeFilter`.
 *
 * Factored out rather than copied because there are now four of these — the
 * page-level date range, the volume charts' day/week/month granularity, the
 * top-senders window and the revenue leaderboard's window — and a second control
 * that looks *almost* like the first reads as a different kind of thing when it
 * isn't. The two leaderboard windows offer the same three options and must keep
 * looking identical; they differ only in what they default to. `DateRangeFilter` keeps its
 * own file: it is the one control that scopes the whole page, and its aria
 * grouping and placement in the page header are part of that meaning.
 *
 * `size="small"` is for a control that lives inside a card header, beside a
 * freshness chip, rather than in the page header.
 */
const SegmentedToggle = <T extends string>({
  options,
  value,
  onChange,
  label,
  size = "small",
}: {
  options: { key: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
  /** Names the group for screen readers — "Granularity", "Window". */
  label: string;
  size?: "small" | "default";
}) => (
  <div
    role="group"
    aria-label={label}
    style={{
      display: "inline-flex",
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      borderRadius: size === "small" ? 6 : 8,
      padding: 2,
      gap: 2,
      flexShrink: 0,
    }}
  >
    {options.map(option => {
      const selected = option.key === value;
      return (
        <button
          key={option.key}
          onClick={() => onChange(option.key)}
          aria-pressed={selected}
          style={{
            ...type.bodySmallMedium,
            fontSize: size === "small" ? 11 : 12,
            color: selected ? colors.ink : colors.inkMuted,
            background: selected ? colors.surfaceMuted : "transparent",
            border: "none",
            borderRadius: 5,
            padding: size === "small" ? "3px 9px" : "6px 12px",
            cursor: "pointer",
            fontWeight: selected ? 600 : 500,
            whiteSpace: "nowrap",
          }}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

export default SegmentedToggle;
